import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  'Practical, honest ways for an individual athlete to fundraise for a season — recurring supporter asks, sponsor letters, community events and more — without giving up a cut of what you raise.';

const FAQS = [
  {
    q: 'How can I fundraise for my sports season as an individual athlete, not a team?',
    a: "Set up your own fund page rather than relying on a team-wide fundraiser. That way friends, family, and your community can back you directly and money goes straight to your family's bank account — you don't have to wait on a club-run campaign or split proceeds with teammates.",
  },
  {
    q: "What's the fastest way to start raising money for a season?",
    a: "Create a free fund page, write one honest paragraph about your season and what the money covers (fees, travel, gear), and text or post the link to the people who already know and support you. Most of the work is the first ask — after that, sharing updates keeps support coming.",
  },
  {
    q: 'Is it better to ask for one big donation or recurring monthly support?',
    a: 'Recurring monthly support tends to be easier to ask for and easier to sustain than one large ask. Ten supporters giving $15–$20 a month across a season adds up to more than most one-time asks, and it covers costs as they come up rather than all at once.',
  },
  {
    q: 'Do traditional team fundraisers like car washes actually raise much?',
    a: "Not much, relative to the effort. Car-wash-style team fundraisers typically net only $1,000–$3,000 for an entire team, and roughly two-thirds of parents say they don't fundraise at all — mostly because the tools are clunky, team-owned, or take a cut of what's raised.",
  },
  {
    q: 'Does Sport Fund take a cut of what I raise?',
    a: 'No. You keep 100% of every donation. A small platform fee (roughly 5–7%) is shown up front and added on top for the supporter at checkout — it is never taken out of what you raise.',
  },
];

const HowToFundraiseForASeason: React.FC = () => {
  usePageMeta('How to Fundraise for Your Sports Season: 7 Honest Ways', ARTICLE_DESCRIPTION, {
    article: true,
    image: '/resources/coach-and-athlete.jpg',
    breadcrumbs: [
      { name: 'Resources', path: '/resources' },
      { name: 'How to Fundraise for Your Sports Season' },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  });
  const TOC_SECTIONS = [
    {
      id: "1-set-up-your-own-fund-page-and-ask-for-recurring-support",
      text: "1. Set up your own fund page — and ask for recurring support"
    },
    {
      id: "2-write-one-honest-specific-update-not-a-generic-ask",
      text: "2. Write one honest, specific update — not a generic ask"
    },
    {
      id: "3-ask-local-businesses-for-a-small-dollar-sponsorship",
      text: "3. Ask local businesses for a small-dollar sponsorship"
    },
    {
      id: "4-time-your-ask-to-a-real-moment-not-a-calendar-reminder",
      text: "4. Time your ask to a real moment, not a calendar reminder"
    },
    {
      id: "5-skip-the-low-payoff-group-event-unless-you-enjoy-running-it",
      text: "5. Skip the low-payoff group event unless you enjoy running it"
    },
    {
      id: "6-check-for-need-based-grants",
      text: "6. Check for need-based grants"
    },
    {
      id: "7-make-sharing-effortless-one-link-everywhere",
      text: "7. Make sharing effortless — one link, everywhere"
    },
    {
      id: "where-sport-fund-fits",
      text: "Where Sport Fund fits"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        How to fundraise for your sports season: 7 honest ways that actually work (2026)
      </h1>
      <p className="text-gray-500 mb-8 text-lg">
        You don't need a club, a booster committee, or a cut taken off the top. Here are practical ways an individual
        athlete can raise real support for a season.
      </p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          Fees, travel, gear, tournament entries — a single season adds up fast, and families are feeling it. The
          average family now spends over <strong>$1,000 a year</strong> on a child's primary sport, up 46% over five
          years, and nearly <strong>1 in 5 parents say they've gone into debt</strong> to keep their kid in the game.
          Yet roughly <strong>two-thirds of families never fundraise at all</strong> — usually because the tools feel
          clunky, team-owned, or take a cut of whatever gets raised.
        </p>
        <p>
          None of the ideas below require a club, a committee, or a big event to pull off. They're ranked roughly by
          effort-to-payoff, honest about what each one actually raises, and built around one idea: the people who
          already believe in you are usually your fastest, cheapest source of support.
        </p>

        <h2 id="1-set-up-your-own-fund-page-and-ask-for-recurring-support">1. Set up your own fund page — and ask for recurring support</h2>
        <p>
          A single $500 ask is hard to make and hard to say yes to. Ten people giving $15–$20 a month across a season
          is easy for them and adds up to more for you — and it keeps covering costs as they come up (travel one
          month, entry fees the next) instead of running out halfway through. This is the highest-leverage move on
          this list because it's the one that keeps working after the first ask. See{' '}
          <Link to="/resources/recurring-vs-one-time-donations" className="text-blue-600">
            recurring vs. one-time donations
          </Link>{' '}
          for the math and exactly how to ask.
        </p>

        <h2 id="2-write-one-honest-specific-update-not-a-generic-ask">2. Write one honest, specific update — not a generic ask</h2>
        <p>
          "Please support me" gets skimmed past. "I made varsity and our first travel meet is in September — here's
          what a season costs and how you can help" gets read. Name the goal, name what the money covers, and share
          it where the people who already know you will actually see it.
        </p>

        <h2 id="3-ask-local-businesses-for-a-small-dollar-sponsorship">3. Ask local businesses for a small-dollar sponsorship</h2>
        <p>
          You don't need a title sponsor. A gym, a physical therapist, a local restaurant, or a family-owned shop
          will often put in $25–$100 in exchange for a shoutout or a logo on your page — especially if you're a
          regular customer or a neighbor. Ask more than one; most say no, a few say yes. See our{' '}
          <Link to="/resources/sponsorship-letter-template" className="text-blue-600">
            sponsorship letter template
          </Link>{' '}
          for exactly what to say.
        </p>

        <h2 id="4-time-your-ask-to-a-real-moment-not-a-calendar-reminder">4. Time your ask to a real moment, not a calendar reminder</h2>
        <p>
          Support flows toward specific moments — making a team, qualifying for a meet, a season kickoff — far more
          than a generic "fundraising season" email. Line your ask up with something that just happened or is about
          to.
        </p>

        <h2 id="5-skip-the-low-payoff-group-event-unless-you-enjoy-running-it">5. Skip the low-payoff group event unless you enjoy running it</h2>
        <p>
          Car-wash-style team fundraisers are a lot of coordinated effort for a typical <strong>$1,000–$3,000 for an
          entire team</strong> — split many ways, that's a small amount per athlete for a full day of work. If your
          team already runs one, join it. But it shouldn't be your main plan; a personal ask usually raises more per
          hour spent.
        </p>

        <h2 id="6-check-for-need-based-grants">6. Check for need-based grants</h2>
        <p>
          Organizations like All Kids Play and various local foundations offer grants specifically to help cover
          registration, gear, and travel costs so kids can keep playing. It costs nothing to check eligibility, and
          it stacks with anything you raise from supporters.
        </p>

        <h2 id="7-make-sharing-effortless-one-link-everywhere">7. Make sharing effortless — one link, everywhere</h2>
        <p>
          The biggest reason people don't give isn't reluctance — it's friction. One clear link you can text, post,
          or hand to a grandparent at Thanksgiving beats a form, a spreadsheet, or a "venmo me" message every time.
          Put it in your bio, text it to family, and re-share it after a milestone.
        </p>

        <h2 id="where-sport-fund-fits">Where Sport Fund fits</h2>
        <p>
          This is exactly why we built Sport Fund: a free fund page that's yours, not your club's. A few things we're
          deliberate about:
        </p>
        <ul>
          <li>
            <strong>You (or your family) own the page</strong> — not a club, not a platform. Payouts go straight to
            your family's bank.
          </li>
          <li>
            <strong>You keep 100% of every donation.</strong> The small platform fee (roughly 5–7%) is added on top
            for the supporter at checkout — never taken out of what you raise. No forced tips, no surprise charges.
          </li>
          <li>
            <strong>Recurring by design</strong>, so small monthly gifts from people who believe in you can quietly
            cover a season's worth of costs.
          </li>
        </ul>
        <p>
          <Link to="/signup-creator" className="text-blue-600">
            Start a free fund page
          </Link>{' '}
          — free for athletes, and you keep 100% of every donation.
        </p>
        <p className="text-sm text-gray-500">
          Sources: Aspen Institute Project Play (2025 survey); New York Life Wealth Watch. Figures are averages and
          vary widely by sport, region, and competition level.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/cost-of-youth-sports-2026" className="text-blue-600">
            How Much Does a Season of Youth Sports Really Cost in 2026?
          </Link>{' '}
          — the real numbers by sport, and honest ways families cover them.
        </p>
        <p>
          <Link to="/resources/fundraising-letter-template" className="text-blue-600">
            Sports Fundraising Letter Template: A Free Letter You Can Copy
          </Link>{' '}
          — a copy-paste letter for the sponsor and supporter asks above.
        </p>
        <p>
          <Link to="/resources/how-to-set-a-fundraising-goal" className="text-blue-600">
            How to Set a Fundraising Goal for Your Sports Season
          </Link>{' '}
          — land on a real number before you send your first ask.
        </p>
        <ArticleFeedback articleSlug="how-to-fundraise-for-a-sports-season" />
      </article>
    </div>
  );
};

export default HowToFundraiseForASeason;
